// src/features/groups/components/GroupList.tsx

import { useMemo } from 'react';
import {
  List,
  TextField,
  DateField,
  NumberField,
  FunctionField,
  useTranslate,
} from 'react-admin';
import { IconButton, Tooltip, Typography, Box } from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import CustomDatagrid from '@/components/common/CustomDatagrid';
import { useGroupPermissions } from '../hooks/useGroupPermissions';
import GroupActions from './GroupActions';
import { compactListSx } from '@/components/common/compactListSx';
import { SectionHeader } from '@/components/common/SectionHeader';
import { FilterPopoverProvider, RaFilterPopover } from '@/components/common/RaFilterPopover';
import { groupsHelpConfig } from '@/config/helpConfig';
import { RegistryLinkedChip } from '@/components/forms/RegistryLinkedChip';

/**
 * Groups list component
 * Displays all groups with filtering and sorting capabilities
 */
export const GroupList = ({ embedded }: { embedded?: boolean }) => {
  const translate = useTranslate();
  const { canShow, canCreate } = useGroupPermissions();
  const navigate = useNavigate();

  const list = (
    <List
      sx={compactListSx}
      actions={false}
      sort={{ field: 'createdAt', order: 'DESC' }}
      perPage={25}
      title={translate('resources.groups.list.title')}
      empty={false}
    >
      <RaFilterPopover searchSource="q" />
      <CustomDatagrid
        bulkActionButtons={false}
        rowClick={canShow ? 'show' : undefined}
        primarySource="name"
        secondarySource="description"
      >
       
         {/* Actions column */}
        <FunctionField
          label=""
          sortable={false}
          render={(record: any) => <GroupActions record={record} />}
        />

        {/* ID (for Keycloak group mapping) */}
        <FunctionField
          source="id"
          label="ID"
          sortable={false}
          render={(record: any) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary' }}>
                {record.id?.length > 12 ? `${record.id.slice(0, 12)}…` : record.id}
              </Typography>
              <Tooltip title="Copiar ID">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(record.id);
                  }}
                  sx={{ p: 0.25 }}
                >
                  <CopyIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        />

        {/* Name */}
        <TextField
          source="name"
          label={translate('resources.groups.fields.name')}
        />
        
        {/* Description */}
        <TextField 
          source="description" 
          label={translate('resources.groups.fields.description')}
        />
        
        {/* Registry link */}
        <FunctionField
          label={translate('resources.groups.fields.registry')}
          sortable={false}
          render={(record: any) => <RegistryLinkedChip record={record} />}
        />

        {/* Hotel Count */}
        <FunctionField
          label={translate('resources.groups.fields.hotelCount')}
          render={(record: any) => record.hotels?.length || 0}
        />
        
        {/* Total Devices */}
        <NumberField 
          source="totalDevices" 
          label={translate('resources.groups.fields.totalDevices')}
          emptyText="0"
        />
        
      

      </CustomDatagrid>
    </List>
  );

  if (embedded) return list;

  return (
    <FilterPopoverProvider>
      <Box sx={{ p: { xs: 1, sm: 1 } }}>
        <SectionHeader
          helpConfig={groupsHelpConfig}
          useRaFilters
          showSearch
          searchPlaceholder={translate('resources.groups.list.search')}
          showCreate={canCreate}
          onCreateClick={() => navigate('/groups/create')}
        />
        {list}
      </Box>
    </FilterPopoverProvider>
  );
};